import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import * as Icons from "lucide-react";
import { Button } from "./ui/button";
import { HEADQUARTERS } from "../mock";

export const Icon = ({ name, ...props }) => {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp {...props} />;
};

/** Fade-up on scroll */
export const Reveal = ({ children, className = "", delay = 0, as: Tag = "div" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <Tag ref={ref} className={`reveal ${visible ? "is-visible" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </Tag>
  );
};

export const Eyebrow = ({ children, light = false, className = "" }) => (
  <div className={`eyebrow ${light ? "text-gold-200" : "text-brand-700"} ${className}`}>
    <span className={`eyebrow-line ${light ? "bg-gold-300" : "bg-brand-500"}`} />
    {children}
  </div>
);

export const PageHero = ({ eyebrow, title, intro, image, children, compact = false }) => (
  <section className={`relative overflow-hidden ${compact ? "min-h-[360px]" : "min-h-[460px]"} flex items-end`} data-testid="page-hero">
    <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/75 to-brand-900/40" />
    <div className="container-x relative z-10 py-14 lg:py-20">
      <Reveal>
        {eyebrow && <Eyebrow light>{eyebrow}</Eyebrow>}
        <h1 className="mt-4 max-w-3xl font-serif text-4xl leading-[1.08] text-white sm:text-5xl lg:text-6xl">{title}</h1>
        {intro && <p className="mt-6 max-w-2xl text-lg leading-relaxed text-sand-200">{intro}</p>}
        {children}
      </Reveal>
    </div>
  </section>
);

export const SectionHeading = ({ eyebrow, title, intro, align = "left", light = false }) => (
  <div className={`${align === "center" ? "mx-auto text-center" : ""} max-w-3xl`}>
    {eyebrow && <Eyebrow light={light} className={align === "center" ? "justify-center" : ""}>{eyebrow}</Eyebrow>}
    <h2 className={`mt-4 font-serif text-3xl leading-tight sm:text-4xl lg:text-[44px] ${light ? "text-white" : "text-ink-900"}`}>{title}</h2>
    {intro && <p className={`mt-5 text-lg leading-relaxed ${light ? "text-sand-200" : "text-ink-600"}`}>{intro}</p>}
  </div>
);

export const SubNav = ({ items }) => (
  <div className="border-b border-brand-100 bg-white/70 backdrop-blur-sm" data-testid="sub-nav">
    <div className="container-x flex gap-1 overflow-x-auto py-2">
      {items.map((i) => (
        <Link
          key={i.path}
          to={i.path}
          className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
            i.active ? "bg-brand-600 text-white" : "text-ink-700 hover:bg-brand-50 hover:text-brand-800"
          }`}
        >
          {i.label}
        </Link>
      ))}
    </div>
  </div>
);

export const NumberedCard = ({ index, title, text, to, icon, delay = 0 }) => (
  <Reveal delay={delay}>
    <Link
      to={to}
      className="card-lift group flex h-full flex-col rounded-3xl border border-brand-100 bg-white p-7 lg:p-8"
      data-testid={`service-card-${index}`}
    >
      <div className="flex items-start justify-between">
        <span className="rounded-full border border-brand-100 bg-brand-50 px-2.5 py-1 font-mono text-[11px] tracking-widest text-brand-700">
          {String(index).padStart(2, "0")}
        </span>
        {icon && (
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 transition-colors duration-300 group-hover:bg-brand-600 group-hover:text-white">
            <Icon name={icon} size={20} />
          </span>
        )}
      </div>
      <h3 className="mt-6 font-serif text-2xl text-ink-900">{title}</h3>
      <p className="mt-3 flex-1 text-[15px] leading-relaxed text-ink-600">{text}</p>
      <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700">
        Scope &amp; details
        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </Link>
  </Reveal>
);

export const ImageCard = ({ index, title, text, to, image, delay = 0 }) => (
  <Reveal delay={delay}>
    <Link to={to} className="group block" data-testid={`audience-card-${index}`}>
      <div className="relative aspect-[4/3] overflow-hidden rounded-3xl">
        <img src={image} alt={title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute left-4 top-4 rounded-full bg-sand-50/90 px-3 py-1 font-mono text-[11px] tracking-widest text-ink-800 backdrop-blur">
          {String(index).padStart(2, "0")}
        </span>
      </div>
      <div className="mt-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-2xl text-ink-900">{title}</h3>
          <p className="mt-2 text-[15px] leading-relaxed text-ink-600">{text}</p>
        </div>
        <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-brand-200 text-brand-700 transition-all duration-300 group-hover:bg-brand-600 group-hover:text-white group-hover:border-brand-600">
          <ArrowUpRight size={16} />
        </span>
      </div>
    </Link>
  </Reveal>
);

export const DetailList = ({ items }) => (
  <div className="divide-y divide-brand-100 rounded-3xl border border-brand-100 bg-white">
    {items.map((it, i) => (
      <Reveal key={it.title} delay={i * 40} className="grid gap-3 p-6 md:grid-cols-12 md:gap-8 lg:p-8">
        <div className="md:col-span-4 flex items-start gap-4">
          <span className="font-mono text-xs tracking-widest text-brand-600 mt-1.5">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="font-serif text-xl text-ink-900">{it.title}</h3>
        </div>
        <p className="md:col-span-8 text-[15px] leading-relaxed text-ink-600">{it.text}</p>
      </Reveal>
    ))}
  </div>
);

export const CTA = ({ title = "Ready to give your nonprofit the Great North Advantage?", text }) => (
  <section className="relative overflow-hidden bg-brand-800" data-testid="cta-section">
    <div className="absolute inset-0 bg-gradient-to-br from-brand-800 to-brand-900" />
    <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-gold-400/15 blur-3xl" />
    <div className="container-x relative grid items-center gap-10 py-20 lg:grid-cols-12 lg:py-28">
      <div className="lg:col-span-8">
        <Eyebrow light>Let's talk</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl leading-tight text-white sm:text-4xl lg:text-5xl">{title}</h2>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-sand-200">
          {text || "Tell us about your nonprofit. A member of our senior management team, not a sales rep, will personally review your request and follow up to discuss next steps."}
        </p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row lg:col-span-4 lg:flex-col lg:items-end">
        <Button asChild size="lg" className="btn-pill h-12 bg-gold-400 px-7 text-ink-900 hover:bg-gold-300" data-testid="cta-contact-btn">
          <Link to="/contact-us">
            Contact Us <ArrowRight size={16} className="ml-2" />
          </Link>
        </Button>
        <Button asChild size="lg" variant="outline" className="btn-pill h-12 border-white/30 bg-transparent px-7 text-white hover:bg-white/10 hover:text-white">
          <a href={`tel:${HEADQUARTERS.phone.replace(/\D/g, "")}`}>
            <Phone size={15} className="mr-2" /> Call {HEADQUARTERS.phone}
          </a>
        </Button>
      </div>
    </div>
  </section>
);
